import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_CONFERENCE_CHAT_HISTORY = gql`
  query GetConferenceChatHistory($conferenceId: ID!) {
    getConferenceChatHistory(conferenceId: $conferenceId) {
      items {
        id
        conferenceId
        senderId
        senderName
        content
        timestamp
      }
      nextToken
    }
  }
`;

interface ChatMessage {
  id: string;
  conferenceId: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: string;
}

interface GetConferenceChatHistoryData {
  getConferenceChatHistory: {
    items: ChatMessage[];
    nextToken: string | null;
  };
}

interface ChatTranscriptProps {
  conferenceId: string;
  conferenceStatus: string;
}

export function ChatTranscript({ conferenceId, conferenceStatus }: ChatTranscriptProps) {
  const { data, loading, error } = useQuery<GetConferenceChatHistoryData>(GET_CONFERENCE_CHAT_HISTORY, {
    variables: { conferenceId },
    skip: conferenceStatus !== 'COMPLETED', // Only fetch for completed conferences
  });

  const formatTime = (timestamp: string): string => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Make URLs clickable
  const renderMessageContent = (content: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = content.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  if (conferenceStatus !== 'COMPLETED') {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Chat Transcript
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-aalaw-red"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Chat Transcript
        </h3>
        <p className="text-red-500 text-sm">Failed to load chat history</p>
      </div>
    );
  }

  const messages: ChatMessage[] = data?.getConferenceChatHistory?.items || [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        Chat Transcript
        {messages.length > 0 && (
          <span className="text-sm font-normal text-gray-500">
            ({messages.length} message{messages.length !== 1 ? 's' : ''})
          </span>
        )}
      </h3>

      {messages.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-4">
          No chat messages were sent during this conference.
        </p>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {messages.map((message, index) => {
            // Show date separator if this is a new day
            const showDateSeparator = index === 0 || 
              formatDate(message.timestamp) !== formatDate(messages[index - 1].timestamp);

            return (
              <div key={message.id}>
                {showDateSeparator && (
                  <div className="flex items-center gap-2 my-3">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs text-gray-400 px-2">
                      {formatDate(message.timestamp)}
                    </span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                )}
                <div className="flex gap-3">
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium flex-shrink-0">
                    {message.senderName.charAt(0).toUpperCase()}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium text-gray-900 text-sm">
                        {message.senderName}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-0.5 break-words">
                      {renderMessageContent(message.content)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
